import http from 'services/http';
import type { AnalysisLanguage } from './language';

export interface AnalysisResult {
  analysis: string;
  /** True when the text had been written by an earlier request. */
  cached: boolean;
}

/** Asks the API to explain one question of the learner's own finished attempt. */
export async function requestAnswerAnalysis(
  attemptId: string,
  questionId: string,
  language: AnalysisLanguage,
  /** A reading passage's sub-question; the passage is `questionId`. */
  subQuestionId?: string,
): Promise<AnalysisResult> {
  const { data } = await http.request.post<{ data: AnalysisResult }>(
    `/api/answer-analysis/attempts/${encodeURIComponent(attemptId)}/questions/${encodeURIComponent(questionId)}`,
    { language, ...(subQuestionId ? { subQuestionId } : {}) },
  );
  return data.data;
}

/**
 * What a candidate gets when they ask for an AI model answer to one Speaking prompt: an analysis (in their language)
 * and a stronger answer in English. IMPROVED is their own answer said one or two CEFR levels higher; SAMPLE (no
 * `currentLevel`) is a strong answer to the question itself, for a prompt left unanswered or answered too little.
 */
export interface SpeakingModelAnswerInfo {
  mode: 'IMPROVED' | 'SAMPLE';
  /** Where the AI judged the candidate to be, A1-C2; null for a sample. */
  currentLevel: string | null;
  targetLevel: string;
  text: string;
  analysis: string;
}

export interface SpeakingModelAnswerResult {
  modelAnswer: SpeakingModelAnswerInfo;
  /** True when the text had been written by an earlier request. */
  cached: boolean;
}

/** Asks the API for the analysis and model answer of one Speaking prompt on the candidate's own finished attempt. */
export async function requestSpeakingModelAnswer(
  attemptId: string,
  questionId: string,
  clipKey: string,
  language: AnalysisLanguage,
): Promise<SpeakingModelAnswerResult> {
  const { data } = await http.request.post<{ data: SpeakingModelAnswerResult }>(
    `/api/answer-analysis/attempts/${encodeURIComponent(attemptId)}/questions/${encodeURIComponent(questionId)}/speaking/${encodeURIComponent(clipKey)}/model-answer`,
    { language },
  );
  return data.data;
}

export interface WritingMarkInfo {
  kind: 'good' | 'suggest' | 'error';
  /** `essay.slice(start, end)` is the marked span of the stored answer text. */
  start: number;
  end: number;
  note: string;
}

/** AI feedback on one Writing task: marks on the learner's essay and a Band 8 answer to the same task. */
export interface WritingFeedbackInfo {
  annotations: WritingMarkInfo[];
  modelAnswer: string;
}

/** Asks the API for the marks and model answer of one Writing task on the candidate's own finished attempt. */
export async function requestWritingFeedback(attemptId: string, questionId: string, language: AnalysisLanguage): Promise<WritingFeedbackInfo> {
  const { data } = await http.request.post<{ data: { feedback: WritingFeedbackInfo } }>(
    `/api/answer-analysis/attempts/${encodeURIComponent(attemptId)}/questions/${encodeURIComponent(questionId)}/writing-feedback`,
    { language },
  );
  return data.data.feedback;
}
